/**
 * studioApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Couche API Studio Pro — fetch natif + axios-compatible.
 *
 * ENDPOINTS ATTENDUS :
 *   GET    /events/:eventId/documents
 *   GET    /events/:eventId/documents/:id
 *   POST   /events/:eventId/documents          body: { name, content }
 *   PUT    /events/:eventId/documents/:id      body: { name, content }
 *   DELETE /events/:eventId/documents/:id
 *   POST   /events/:eventId/documents/:id/fork-page  body: { pageId, newName }
 */

class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function createStudioApi({ baseUrl, token, eventId }) {
  const headers = () => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  });

  async function request(method, path, body) {
    const res = await fetch(`${baseUrl}${path}`, {
      method,
      headers: headers(),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 204) return null;
    if (!res.ok) {
      let msg = `API Error ${res.status}`;
      try {
        const d = await res.json();
        msg = d.message || d.error || msg;
      } catch {}
      throw new ApiError(res.status, msg);
    }

    const data = await res.json();
    return data.data ?? data;
  }

  return {
    /** Lister les documents de l'utilisateur pour un événement */
    getDocuments: () => request('GET', `/events/${eventId}/documents`),
    /** Charger un document */
    getDocument: (id) => request('GET', `/events/${eventId}/documents/${id}`),
    /** Créer un nouveau document */
    createDocument: (name, content) =>
      request('POST', `/events/${eventId}/documents`, { name, content }),
    /** Mettre à jour un document */
    updateDocument: (id, name, content) =>
      request('PUT', `/events/${eventId}/documents/${id}`, { name, content }),
    /** Supprimer un document */
    deleteDocument: (id) =>
      request('DELETE', `/events/${eventId}/documents/${id}`),
    /**
     * Forker une page dans un nouveau document indépendant.
     * Le serveur crée une copie avec forked_from = docId
     */
    forkPage: (docId, pageId, newName) =>
      request('POST', `/events/${eventId}/documents/${docId}/fork-page`, {
        page_id: pageId,
        name: newName,
      }),
  };
}

export { createStudioApi, ApiError };
